import { HttpRequestError } from "../../auth/HttpInterceptor";
import { deleteHttp, get } from "../../common/api/apiShared";
import { Organization } from "../organizations/Organization.types";
import { ApplicationUser, ConfigurableApplicationUser, MenuAccess, MenuOption, UserRole } from "./User.types";

export const getAllowedOrganizations = (): Promise<Organization[]> => get<Organization[]>("/api/users/me/allowed-organizations");

export const saveUser = async (modifiedUser: ApplicationUser): Promise<void> => {
    try {
        await fetch("/api/users", {
            method: "put",
            body: JSON.stringify(modifiedUser),
            headers: {
                "Content-Type": "application/json",
            },
        });
    } catch (error) {
        if (error instanceof HttpRequestError) {
            const errorText = await error.response.text();
            throw new Error(errorText || `Failed to save user (Status: ${error.statusCode})`);
        }
        throw error;
    }
};

export const resendInvitationMail = (userToSendTo: ApplicationUser): Promise<void> =>
    fetch(`/api/users/${userToSendTo.email}/resend-invitation-mail`, {
        method: "post",
    }).then(() => {});

export const deleteUser = (userId: Number) =>
    deleteHttp(`/api/users/${userId}`)
        .catch((response) => {
            if (response.statusCode === 500) return response.response.json();
            return response;
        })
        .then((response) => {
            if (response.status === 500) throw new Error(response.detail);
        });
export const mapApplicationUserToConfigurableUser = (user: ApplicationUser): ConfigurableApplicationUser => ({
    name: user.name,
    email: user.email,
    phoneNumber: user.phoneNumber,
    countryCode: user.countryCode,
    userRoles: user.userRoles,
    organizations: user.organizations,
    countryAccess: user.countryAccess || [],
    menuAccess: user.menuAccess || [],
    new: false,
    id: user.id,
});

export const userHasOrganizationAccess = (user: ApplicationUser | null) => (user ? user.organizations.length >= 1 : false);

export const userHasAccessToMultipleOrganizations = (user: ApplicationUser | null) => (user ? user.organizations.length > 1 : false);

export const userIsAdmin = (user: ApplicationUser | null) => (user ? user.userRoles.some((r) => r.name === "Admin") : false);

export const initialConfigurableUser: ConfigurableApplicationUser = {
    name: "",
    email: "",
    phoneNumber: "",
    userRoles: [],
    organizations: [],
    countryCode: "",
    countryAccess: [],
    menuAccess: [],
    new: true,
};

export const getUserRoles: UserRole[] = [
    { name: "Employee", id: 0 },
    { name: "Client", id: 1 },
    { name: "Admin", id: 2 },
    { name: "Read only", id: 3 },
];

export const getMenuOptions = (): MenuOption[] => [
    { id: MenuAccess.Trademarks, name: "Trademarks" },
    { id: MenuAccess.Designs, name: "Designs" },
    { id: MenuAccess.Copyrights, name: "Copyrights" },
    { id: MenuAccess.Watches, name: "Watches" },
    { id: MenuAccess.Renewals, name: "Renewals" },
    { id: MenuAccess.Disputes, name: "Disputes" },
    { id: MenuAccess.Costs, name: "Costs" },
    { id: MenuAccess.PriceCalculator, name: "Price Calculator" },
];

export const getCountryOptions = () =>
    [
        { code: "ad", name: "Andorra" },
        { code: "ae", name: "United Arab Emirates" },
        { code: "af", name: "Afghanistan" },
        { code: "ag", name: "Antigua & Barbuda" },
        { code: "ai", name: "Anguilla" },
        { code: "al", name: "Albania" },
        { code: "am", name: "Armenia" },
        { code: "an", name: "Netherlands Antilles" },
        { code: "ao", name: "Angola" },
        { code: "ar", name: "Argentina" },
        { code: "at", name: "Austria" },
        { code: "au", name: "Australia" },
        { code: "aw", name: "Aruba" },
        { code: "az", name: "Azerbaijan" },
        { code: "ba", name: "Bosnia & Herzegovina" },
        { code: "bb", name: "Barbados" },
        { code: "bd", name: "Bangladesh" },
        { code: "bg", name: "Bulgaria" },
        { code: "bh", name: "Bahrain" },
        { code: "bi", name: "Burundi" },
        { code: "bm", name: "Bermuda" },
        { code: "bn", name: "Brunei Darussalam" },
        { code: "bo", name: "Bolivia" },
        { code: "bq", name: "Bonaire, Saint Eustatius and Saba" },
        { code: "br", name: "Brazil" },
        { code: "bs", name: "Bahamas" },
        { code: "bt", name: "Bhutan" },
        { code: "bw", name: "Botswana" },
        { code: "bx", name: "Benelux" },
        { code: "by", name: "Belarus" },
        { code: "bz", name: "Belize" },
        { code: "c5", name: "Turkish Cyprus" },
        { code: "ca", name: "Canada" },
        { code: "cd", name: "Congo, Democratic Republic of" },
        { code: "cg", name: "Congo" },
        { code: "ch", name: "Switzerland" },
        { code: "ck", name: "Cook Islands" },
        { code: "cl", name: "Chile" },
        { code: "cn", name: "China" },
        { code: "co", name: "Colombia" },
        { code: "cr", name: "Costa Rica" },
        { code: "cs", name: "Serbia & Montenegro" },
        { code: "cu", name: "Cuba" },
        { code: "cv", name: "Cape Verde" },
        { code: "cw", name: "Curaçao" },
        { code: "cy", name: "Cyprus" },
        { code: "cz", name: "Czechia" },
        { code: "de", name: "Germany" },
        { code: "dj", name: "Djibouti" },
        { code: "dk", name: "Denmark" },
        { code: "dm", name: "Dominica" },
        { code: "do", name: "Dominican Republic" },
        { code: "dz", name: "Algeria" },
        { code: "ec", name: "Ecuador" },
        { code: "ee", name: "Estonia" },
        { code: "eg", name: "Egypt" },
        { code: "es", name: "Spain" },
        { code: "et", name: "Ethiopia" },
        { code: "eu", name: "European Union" },
        { code: "fi", name: "Finland" },
        { code: "fj", name: "Fiji" },
        { code: "fk", name: "Falkland Islands (Malvinas)" },
        { code: "fr", name: "France" },
        { code: "gb", name: "United Kingdom" },
        { code: "gd", name: "Grenada" },
        { code: "ge", name: "Georgia" },
        { code: "gg", name: "Guernsey" },
        { code: "gh", name: "Ghana" },
        { code: "gi", name: "Gibraltar" },
        { code: "gm", name: "Gambia" },
        { code: "gr", name: "Greece" },
        { code: "gt", name: "Guatemala" },
        { code: "gu", name: "Guam" },
        { code: "gy", name: "Guyana" },
        { code: "gz", name: "Gaza" },
        { code: "hk", name: "Hong Kong" },
        { code: "hn", name: "Honduras" },
        { code: "hr", name: "Croatia" },
        { code: "ht", name: "Haiti" },
        { code: "hu", name: "Hungary" },
        { code: "id", name: "Indonesia" },
        { code: "ie", name: "Ireland" },
        { code: "ii", name: "International Agreement (Madrid)" },
        { code: "il", name: "Israel" },
        { code: "im", name: "International Design Registration" },
        { code: "in", name: "India" },
        { code: "ip", name: "International Protocol (Madrid)" },
        { code: "iq", name: "Iraq" },
        { code: "ir", name: "Iran, Islamic Republic of" },
        { code: "is", name: "Iceland" },
        { code: "it", name: "Italy" },
        { code: "je", name: "Jersey" },
        { code: "jm", name: "Jamaica" },
        { code: "jo", name: "Jordan" },
        { code: "jp", name: "Japan" },
        { code: "ke", name: "Kenya" },
        { code: "kg", name: "Kyrgyzstan" },
        { code: "kh", name: "Cambodia" },
        { code: "kn", name: "Saint Kitts and Nevis" },
        { code: "ko", name: "Kosovo" },
        { code: "kp", name: "Korea, Democratic People's Republic of" },
        { code: "kr", name: "Korea, Republic of" },
        { code: "kw", name: "Kuwait" },
        { code: "ky", name: "Cayman Islands" },
        { code: "kz", name: "Kazakhstan" },
        { code: "la", name: "Lao People's Democratic Republic" },
        { code: "lb", name: "Lebanon" },
        { code: "lc", name: "Saint Lucia" },
        { code: "li", name: "Liechtenstein" },
        { code: "lk", name: "Sri Lanka" },
        { code: "lr", name: "Liberia" },
        { code: "ls", name: "Lesotho" },
        { code: "lt", name: "Lithuania" },
        { code: "lv", name: "Latvia" },
        { code: "ly", name: "Libya" },
        { code: "m9", name: "Malaya" },
        { code: "ma", name: "Morocco" },
        { code: "mc", name: "Monaco" },
        { code: "md", name: "Moldova, Republic of" },
        { code: "me", name: "Montenegro" },
        { code: "mg", name: "Madagascar" },
        { code: "mk", name: "North Macedonia" },
        { code: "mm", name: "Myanmar" },
        { code: "mn", name: "Mongolia" },
        { code: "mo", name: "Macau" },
        { code: "ms", name: "Montserrat" },
        { code: "mt", name: "Malta" },
        { code: "mu", name: "Mauritius" },
        { code: "mv", name: "Maldives" },
        { code: "mw", name: "Malawi" },
        { code: "mx", name: "Mexico" },
        { code: "my", name: "Malaysia" },
        { code: "mz", name: "Mozambique" },
        { code: "na", name: "Namibia" },
        { code: "ng", name: "Nigeria" },
        { code: "ni", name: "Nicaragua" },
        { code: "nl", name: "Netherlands" },
        { code: "no", name: "Norway" },
        { code: "np", name: "Nepal" },
        { code: "nz", name: "New Zealand" },
        { code: "oa", name: "OAPI" },
        { code: "om", name: "Oman" },
        { code: "pa", name: "Panama" },
        { code: "pe", name: "Peru" },
        { code: "pf", name: "French Polynesia" },
        { code: "pg", name: "Papua New Guinea" },
        { code: "ph", name: "Philippines" },
        { code: "pk", name: "Pakistan" },
        { code: "pl", name: "Poland" },
        { code: "pr", name: "Puerto Rico" },
        { code: "pt", name: "Portugal" },
        { code: "py", name: "Paraguay" },
        { code: "qa", name: "Qatar" },
        { code: "ro", name: "Romania" },
        { code: "rs", name: "Serbia" },
        { code: "ru", name: "Russian Federation" },
        { code: "rw", name: "Rwanda" },
        { code: "s3", name: "Sabah" },
        { code: "s4", name: "Sarawak" },
        { code: "sa", name: "Saudi Arabia" },
        { code: "sb", name: "Solomon Islands" },
        { code: "sc", name: "Seychelles" },
        { code: "sd", name: "Sudan" },
        { code: "se", name: "Sweden" },
        { code: "sg", name: "Singapore" },
        { code: "sh", name: "Saint Helena, Ascension and Tristan da Cunha" },
        { code: "si", name: "Slovenia" },
        { code: "sk", name: "Slovakia" },
        { code: "sl", name: "Sierra Leone" },
        { code: "sm", name: "San Marino" },
        { code: "so", name: "Somalia" },
        { code: "sr", name: "Suriname" },
        { code: "st", name: "Sao Tome and Principe" },
        { code: "sv", name: "El Salvador" },
        { code: "sx", name: "Sint Maarten (Dutch Part)" },
        { code: "sy", name: "Syrian Arab Republic" },
        { code: "sz", name: "Swaziland" },
        { code: "t1", name: "Tanzania (Mainland)" },
        { code: "ta", name: "Tangier" },
        { code: "tc", name: "Turks and Caicos Islands" },
        { code: "tg", name: "Togo" },
        { code: "th", name: "Thailand" },
        { code: "tj", name: "Tajikistan" },
        { code: "tl", name: "Timor Leste" },
        { code: "tm", name: "Turkmenistan" },
        { code: "tn", name: "Tunisia" },
        { code: "to", name: "Tonga" },
        { code: "tr", name: "Turkey" },
        { code: "tt", name: "Trinidad and Tobago" },
        { code: "tw", name: "Taiwan" },
        { code: "tz", name: "Tanzania, United Republic of" },
        { code: "ua", name: "Ukraine" },
        { code: "ug", name: "Uganda" },
        { code: "us", name: "United States" },
        { code: "uy", name: "Uruguay" },
        { code: "uz", name: "Uzbekistan" },
        { code: "vc", name: "Saint Vincent & the Grenadines" },
        { code: "ve", name: "Venezuela" },
        { code: "vg", name: "Virgin Islands, British" },
        { code: "vi", name: "Virgin Islands, US" },
        { code: "vn", name: "Viet Nam" },
        { code: "vu", name: "Vanuatu" },
        { code: "we", name: "West Bank" },
        { code: "wo", name: "Madrid Protocol" },
        { code: "ws", name: "Samoa" },
        { code: "ww", name: "World Wide" },
        { code: "ye", name: "Yemen" },
        { code: "z1", name: "Zanzibar" },
        { code: "za", name: "South Africa" },
        { code: "zm", name: "Zambia" },
        { code: "zw", name: "Zimbabwe" },
    ].sort((a, b) => a.name.localeCompare(b.name));

export const getFilteredMenuOptions = (userRoles: UserRole[]) => {
    const allMenuOptions = getMenuOptions();
    const isClient = userRoles.some((role) => role.name === "Client");
    const isReadOnly = userRoles.some((role) => role.name === "Read only");

    const filteredMenus = allMenuOptions.filter((menu) => {
        if (isClient && menu.id === MenuAccess.PriceCalculator) return false;
        if (isReadOnly && (menu.id === MenuAccess.Costs || menu.id === MenuAccess.PriceCalculator)) return false;
        return true;
    });

    return filteredMenus.map((menu) => ({
        label: menu.name,
        value: menu,
    }));
};
